import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { KeywordDataService } from '../services/keyword-data.service';
import { LU_Keyword } from '../models/keyword';
import { StarbaseModalButton, StarbaseModalDialogService, StarbaseModalOptions, StarbaseModalTypes } from 'starbase-modal-dialog';
import { StarbaseNotificationSentiment, StarbaseNotificationService } from 'starbase-notification';

export type SynonymizationMode = 'synonyms' | 'hierarchy';

@Component({
  selector: 'app-synonyms-editor',
  templateUrl: './synonyms-editor.component.html',
  styleUrls: ['./synonyms-editor.component.css']
})
export class SynonymsEditorComponent implements OnInit, OnDestroy {
  @ViewChild('search') searchInput: ElementRef<HTMLInputElement>;
  @ViewChild('relatedList') relatedListDiv: ElementRef<HTMLDivElement>;
  @ViewChild('keywordsList') keywordsListDiv: ElementRef<HTMLDivElement>;
  @ViewChild('lastRelatedTabStop') lastRelatedTabStop: ElementRef<HTMLDivElement>;
  @ViewChild('saveButtonTabStop') saveButtonTabStop: ElementRef<HTMLDivElement>;

  public searchQuery = '';
  public isLoading = true;
  public isModeHierarchy = false;
  public activeKeyword: LU_Keyword;
  public keywords: LU_Keyword[] = [];
  public filteredKeywords: LU_Keyword[] = [];
  public page = 0;
  public relatedWordsPage = 0;
  public keywordsPerPage = 18;

  public defaultRelatedKeywords: number[] = [];
  public relatedKeywords: number[] = [];
  public markedForRemoval: number[] = [];
  public markedForAddition: number[] = [];

  public editorMode: SynonymizationMode = 'synonyms';

  // eslint-disable-next-line @typescript-eslint/ban-types
  private dismoundHandlers: Function[] = [];

  constructor(
    public keywordSvc: KeywordDataService,
    protected modalSvc: StarbaseModalDialogService,
    protected notiSvc: StarbaseNotificationService,
    private cd: ChangeDetectorRef) {}

  public get numberPages() {
    return Math.ceil(this.filteredKeywords.length / this.keywordsPerPage);
  }
  public get numberRelatedPages() {
    return Math.ceil(this.relatedKeywords.length / this.keywordsPerPage);
  }
  public get numChangesMade() {
    return this.markedForAddition.length + this.markedForRemoval.length;
  }
  public get canAddRelated() {
    return this.activeKeyword && !this.isLoading && !this.relatedKeywords.includes(this.activeKeyword.uid);
  }
  public get canRemoveRelated() {
    return this.activeKeyword && !this.isLoading && this.relatedKeywords.includes(this.activeKeyword.uid);
  }

  // Keywords visable on the left side pane to select from ( Uses Search Query )
  public get visableKeywords() {
    const startIndex = this.keywordsPerPage * this.page;

    this.filteredKeywords = this.keywords.filter(kw => {
      const isSelected = kw.keyword === this.selectedKeyword.keyword;
      const isRelated = this.relatedKeywords.includes(kw.uid);
      const matchesSearch = kw.keyword.toLowerCase().includes(this.searchQuery.toLowerCase());
      return (!isRelated && !isSelected && matchesSearch); // Dont return related or selected keywords for search
    });

    return this.filteredKeywords.slice(startIndex, startIndex + this.keywordsPerPage);
  }

  // The array of all possible related/child keywords to be displayed. Not the actual list which is displayed.
  public get relatedKeywordView() {
    return this.keywords.filter(kw => this.relatedKeywords.includes(kw.uid));
  }
  // Array of keywords which are visable on the right-hand side for related/children.
  public get visableRelatedKeywords() {
    const startIndex = this.keywordsPerPage * this.relatedWordsPage;
    return this.relatedKeywordView.slice(startIndex, startIndex + this.keywordsPerPage);
  }

  // The keyword which we are using to mark related / hierarchical keywords
  public get selectedKeyword()  {
    return this.keywordSvc.selectedKeyword;
  }

  ngOnInit(): void {
    this.keywordSvc.getAllSynonymAndChildrenData().then(data => {
      this.keywords = data;
      this.getSynonyms();
      this.searchInput.nativeElement.focus();
    }).catch(err => {
      console.error(err);
      this.closeModal();
    });

    this.setupTabbableKeywords();
  }

  ngOnDestroy(): void {
    this.dismoundHandlers.forEach(handler => handler());
  }



  public getSynonyms(doneCB?: () => void) {
    this.relatedKeywords = this.keywordSvc.getKeywordSynonyms(this.selectedKeyword.uid).map(s => s.uid);
    this.defaultRelatedKeywords = [...this.relatedKeywords];
    this.isLoading = false;
    this.isModeHierarchy = false;

    if (doneCB) {
      doneCB();
    }
  }

  public getKeywordChildren(doneCB?: () => void) {
    this.relatedKeywords = this.keywordSvc.getKeywordChildren(this.selectedKeyword.uid).map(s => s.uid);
    this.defaultRelatedKeywords = [...this.relatedKeywords];
    this.isLoading = false;
    this.isModeHierarchy = true;

    if (doneCB) {
      doneCB();
    }
  }

  // Sets the currently highlighted keyword from the two panes. Allows user to move the keyword over
  public selectKeyword(kw: LU_Keyword) {
    this.activeKeyword = kw;
    this.focusSelectedKeyword();
  }

  // Add a keyword from the left-hand side to the right as a related/child keyword.
  public addRelated() {
    if (this.canAddRelated) {
      this.relatedKeywords.push(this.activeKeyword.uid);
      this.markedForRemoval = this.markedForRemoval.filter(kw => kw !== this.activeKeyword.uid);

      if (!this.defaultRelatedKeywords.includes(this.activeKeyword.uid)) {
        this.markedForAddition.push(this.activeKeyword.uid);
      }

      this.focusSelectedKeyword();
    }
  }

  // Moves a keyword from the relates/children view on the right to the left pool of keywords
  public removeRelated() {
    if (this.canRemoveRelated) {
      this.markedForAddition = this.markedForAddition.filter(kw => kw !== this.activeKeyword.uid);
      this.relatedKeywords = this.relatedKeywords.filter(kw => kw !== this.activeKeyword.uid);

      if (this.defaultRelatedKeywords.includes(this.activeKeyword.uid)) {
        this.markedForRemoval.push(this.activeKeyword.uid);
      }

      this.focusSelectedKeyword();
    }

    if (this.relatedWordsPage  && this.relatedWordsPage + 1 > this.numberRelatedPages) {
      this.changePage('related', this.numberRelatedPages - 1);
    }
  }

  // Resets the addition/subtraction arrays and attempts to update the server with the new changes.
  public async saveChanges() {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    const newRelatedKeywords = [...this.relatedKeywords];
    this.defaultRelatedKeywords = [...newRelatedKeywords];
    this.relatedKeywords = [...newRelatedKeywords];

    // Send Marked for Removal and Marked for Addition both inside a POST
    const removals = this.markedForRemoval.map(kw => kw);
    const additions = this.markedForAddition.map(kw => kw);

    const onSuccess = () => {
      this.isLoading = false;
      this.notiSvc.postNotification(this.notiSvc.notificationBuilder(
        'Changes Saved', 'Your changes have been saved to the server!',
        undefined, 5000, StarbaseNotificationSentiment.Success
      ));

      this.markedForRemoval = [];
      this.markedForAddition = [];
      this.activeKeyword = null;
    };

    const onError = (e: any) => {
      const err = e?.serverMsg?.at(0) as string | undefined;
      const message = err ? err : 'Saving could not be completed. Please try again later!';

      this.isLoading = false;
      this.notiSvc.postNotification(this.notiSvc.notificationBuilder(
        'Something Went Wrong', message,
        undefined, 8000, StarbaseNotificationSentiment.Error
      ));
    };

    const updateMode = this.isModeHierarchy ? 'hierarchy' : 'synonyms';
    this.keywordSvc.updateKeywordSynonymization(removals, additions, this.selectedKeyword.uid, updateMode)
      .then(onSuccess)
      .catch(onError)
      .finally(() => this.isLoading = false);
  }

  public cancelChanges(callback?: (closing: boolean) => void) {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    const yesBtn = new StarbaseModalButton('Proceed', () => {
      // Revert changes back to default state
      this.markedForAddition = [];
      this.markedForRemoval = [];
      this.relatedKeywords = this.defaultRelatedKeywords.map(_ => _);

      this.modalSvc.closeModal('done');
      this.isLoading = false;
      this.searchInput.nativeElement.focus();
      if (callback) {
        callback(true);
      }
    });

    const cancelBtn = new StarbaseModalButton('Back', () => {
      this.isLoading = false;
      this.modalSvc.closeModal('done');
      if (callback) {
        callback(false);
      }
    });

    const cancelModal = new StarbaseModalOptions(
      'Unsaved Changes!',
      `You have ${this.numChangesMade} unsaved changes. Do you wish to proceed?`,
      [yesBtn, cancelBtn], StarbaseModalTypes.Warning);

    cancelModal.dismissable = false;
    this.modalSvc.postModal(cancelModal);
  }

  public closeModal() {
    if (this.numChangesMade === 0) {
      this.keywordSvc.hideSynonymizationModal(); return;
    }

    this.cancelChanges((closeSelected) => {
      if (closeSelected) {
        this.keywordSvc.hideSynonymizationModal();
      } else {
        this.isLoading = false;
      }
    });
  }

  public changePage(kind: 'related' | 'keywords', pageNum: number) {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    kind === 'keywords' ? this.page = pageNum : this.relatedWordsPage = pageNum;
  }

  public searchChanged() {
    this.page = 0;
  }

  public toggleEditingMode() {
    let ignorePossibleChanges = true;
    const handleEditingModeChange = () => {
      // Make sure user wants to ignore changes before doing anything
      if (ignorePossibleChanges) {

        this.isModeHierarchy = !this.isModeHierarchy;

        this.isLoading = true;
        this.markedForAddition = [];
        this.markedForRemoval = [];

        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        this.isModeHierarchy ?
          this.getKeywordChildren() :
          this.getSynonyms();

      }
    };

    if (this.numChangesMade > 0) {
      this.cancelChanges(res => {
        ignorePossibleChanges = res;
        handleEditingModeChange();
      });
    } else {
      handleEditingModeChange();
    }
  }
  // Provides all handling for a clean tabbing support. Has to handle extra cases to prevent going out of the document at times.
  private setupTabbableKeywords() {
    const handler = (ev: KeyboardEvent) => {
      const t = document.activeElement;

      // When a element in one of the two keyword lists gains focus we set it to the active keyword which then also has to re-apply focus
      // if an enter key is pressed.
      if (t instanceof HTMLElement && 'uid' in t.dataset) {
        // When user selects a keyword and hits esc should de-select it
        if (ev.key === 'Escape"') {
          this.activeKeyword = null;
          this.searchInput.nativeElement.focus();
          return;
        }

        const uid = +t.dataset.uid;
        this.selectKeyword(this.keywordSvc.getKeyword(uid));

        if (ev.key === 'Enter') {
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          this.canAddRelated ? this.addRelated() : this.removeRelated();
        }

        return;
      }

      // If nothing is selected and esc is pressed then exit modal
      if (ev.key === 'Escape') {
        return this.closeModal();
      }

      // This will run following the blur event which we need
      requestAnimationFrame(() => {
        //When user tabs after the last element we need to shift to the save button focus. Without this the tab would exit the document view
        if (document.activeElement.isSameNode(this.lastRelatedTabStop.nativeElement)) {
          let selectedButton = false;
          this.activeKeyword = null;
          document.querySelectorAll('.save-tab-group').forEach((e, i) => {
            const btn = e as HTMLButtonElement;
            btn.tabIndex = 2;
            if ( !selectedButton && !btn.disabled && i !== 0) {
              if (btn.firstElementChild instanceof HTMLButtonElement) {
                btn.firstElementChild.focus();
              } else {
                btn.focus();
              }

              selectedButton = true;
            }
          });

          // eslint-disable-next-line max-len
          // If neither save/undo/next/back buttons can be clicked then go all the way to the start of focus at the input field after clearing the tab index
          if (!selectedButton) {
            document.querySelectorAll('.save-tab-group').forEach(e => (e as HTMLButtonElement).tabIndex = -1);
            this.activeKeyword = null;
            this.searchInput.nativeElement.focus();
          }
        } else if (document.activeElement.isSameNode(this.saveButtonTabStop.nativeElement)) {
          //When user tabs back from the save button go into related keywords.If there are no related keywords then go into the left display
          if (this.relatedKeywords.length > 0) {
            const children = this.relatedListDiv.nativeElement.children;
            const lastEl = children[children.length - 2] as HTMLDivElement;
            const uid = +lastEl.dataset.uid;
            this.selectKeyword(this.keywordSvc.getKeyword(uid));
          } else {
            const lchildren = this.keywordsListDiv.nativeElement.children;
            if (lchildren.length > 0) {
              const lastLeftElUID = +(lchildren[lchildren.length -1 ] as HTMLDivElement).dataset.uid;
              this.selectKeyword(this.keywordSvc.getKeyword(lastLeftElUID));
            } else {
              this.searchInput.nativeElement.focus(); // If no keywords can be selected then default to search field
            }
          }
        }
      });
    };

    window.addEventListener('keyup', handler);
    this.dismoundHandlers.push(() => window.removeEventListener('keyup', handler));
  }

  // Will track the selected keyword and make sure it is ALWAYS in view.
  private focusSelectedKeyword() {
    // To allow blur event to pass by
    requestAnimationFrame(() => {
      let el = document.querySelector('.syn-selected') as HTMLElement;
      if (el) {
        el.focus(); return;
      }

      // eslint-disable-next-line max-len
      // This means the element tabbed/was swapped out of view. The steps below are to find the correct page index to go to to keep it in view.
      if (this.canAddRelated) {
        // The element was sent from right -> left
        const indexOfSelected = this.filteredKeywords.map(a => a.uid).indexOf(this.activeKeyword.uid);
        const pageToViewIndex = Math.floor(indexOfSelected / this.keywordsPerPage);
        this.changePage('keywords', pageToViewIndex);
      } else {
        const indexOfSelected = this.relatedKeywordView.map(a => a.uid).indexOf(this.activeKeyword.uid);
        const pageToViewIndex = Math.floor(indexOfSelected / this.keywordsPerPage);
        this.changePage('related', pageToViewIndex);
      }

      el = document.querySelector('.syn-selected') as HTMLElement;
      el?.focus();
      this.cd.detectChanges();
    });
  }
}

